import { useEffect, useState } from "react";
import { doc, getDoc, getFirestore, updateDoc } from "firebase/firestore";
import { app } from "../firebaseConfig";
import authorImage from "../assets/images/carousel-1.jpg";
import OptimizedImage from "../components/OptimizedImage";
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Avatar,
  useTheme,
} from "@mui/material";
import {
  Facebook,
  WhatsApp,
  Instagram,
  CalendarToday,
  Comment as CommentIcon,
} from "@mui/icons-material";
import CommentForm from "../components/CommentForm";
import YoutubePlayer from "../components/YoutubePlayer";
import CustomAudioPlayer from "../components/AudioPlayer";
import { renderKoToast, renderOkToast } from "../utils/functionUtils";
import moment from "moment";

// UI Components
import Header from "../components/Header";
import Footer from "../components/Footer";
import BreadCrumb from "../components/Breadcrumb";
import SEO from "../components/SEO";

const getArticleIdFromPath = () => {
  const parts = window.location.pathname.split("/");
  const lastPart = parts[parts.length - 1];
  // Se l'URL è nel formato /blog/titolo-articolo-id, estrai solo l'ID
  if (lastPart.includes("-")) {
    return lastPart.split("-").pop();
  }
  return lastPart; // Fallback per i vecchi URL
};

const Article = () => {
  const [model, setModel] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hoveredComment, setHoveredComment] = useState(null);
  const db = getFirestore(app);
  const theme = useTheme();

  // Funzione per ottenere le iniziali dal nome e cognome
  const getInitials = (firstName = "", lastName = "") => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Funzione per formattare la data in italiano
  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date.toDate()).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    (async () => {
      const id = getArticleIdFromPath();
      const docRef = doc(db, "articles", id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setModel(docSnap.data());
      } else {
        renderKoToast("No such document!");
      }
    })();
  }, [db]);

  const shareOnWhatsapp = () => {
    const articleUrl = encodeURIComponent(window.location.href);
    const message = encodeURIComponent("Guarda questo articolo interessante:");
    const whatsappUrl = `https://wa.me/?text=${message} ${articleUrl}`;
    window.open(whatsappUrl, "_blank");
  };

  const shareOnFacebook = () => {
    const articleUrl = encodeURIComponent(window.location.href);
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${articleUrl}`;

    window.open(facebookUrl, "_blank");
  };

  const shareOnInstagram = () => {
    const articleUrl = window.location.href;
    const articleTitle = document.title;

    // Verifica se è mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile && navigator.share) {
      // Su mobile usa Web Share API
      navigator
        .share({
          title: articleTitle,
          text: "Dai un'occhiata a questo articolo!",
          url: articleUrl,
        })
        .catch(console.error);
    } else {
      // Su desktop o fallback
      const textToShare = `${articleTitle}\n\n${articleUrl}`;

      if (navigator.clipboard) {
        navigator.clipboard.writeText(textToShare).then(() => {
          alert("Testo copiato! Ora puoi incollarlo in un post Instagram");
        });
      } else {
        // Fallback per browser molto vecchi
        prompt("Copia questo testo per Instagram:", textToShare);
      }
    }
  };

  const submitComment = async (commentData) => {
    if (!commentData) return;

    setIsSubmitting(true);
    const comment = {
      ...commentData,
      timestamp: new Date(),
      published: false,
      checked: false,
    };

    try {
      const docRef = doc(db, "articles", model.id);
      const updatedArticle = { ...model };
      updatedArticle.comments = [...(updatedArticle.comments || []), comment];

      await updateDoc(docRef, {
        comments: updatedArticle.comments,
      });

      setModel(updatedArticle);
      renderOkToast(
        "Commento inviato! Sarà visibile dopo l'approvazione del moderatore."
      );
      return true;
    } catch (error) {
      console.error("Error adding comment:", error);
      renderKoToast("Si è verificato un errore durante l'invio del commento.");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO title={model?.title} />
      <Header />
      <section id="article">
        <Box
          sx={{ maxWidth: "800px", mx: "auto", px: { xs: 1, sm: 3, md: 4 } }}
        >
          <Box sx={{ mb: 4 }}>
            <BreadCrumb customTitle={model?.title} />
          </Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontSize: { xs: "1.8rem", sm: "2.125rem" },
              fontWeight: 600,
              lineHeight: 1.2,
              mb: 2,
              textAlign: "center",
              color: "text.primary",
            }}
          >
            {model?.title}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              my: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "text.secondary",
              }}
            >
              <CalendarToday fontSize="small" />
              <span>
                {moment(
                  model?.date?.seconds * 1000 +
                    Math.round(model?.date?.nanoseconds / 1e6)
                ).format("DD MMMM YYYY")}
              </span>
            </Box>
          </Box>
          {/* Immagine in posizione start */}
          {model?.image?.position === "start" && model?.image?.media && (
            <Box
              sx={{
                my: 4,
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: 3,
                display: "inline-block",
                maxWidth: "100%",
                mx: "auto",
                lineHeight: 0,
              }}
            >
              <OptimizedImage
                src={model.image.media}
                alt={model.title}
                width={"auto"}
                height={"auto"}
                loading="lazy"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  display: "block",
                  transition: "transform 0.3s ease-in-out",
                }}
                imgProps={{
                  onMouseOver: (e) =>
                    (e.currentTarget.style.transform = "scale(1.02)"),
                  onMouseOut: (e) =>
                    (e.currentTarget.style.transform = "scale(1)"),
                }}
              />
            </Box>
          )}

          {/* Audio in posizione start */}
          {model?.audio?.position === "start" && model?.audio?.media && (
            <CustomAudioPlayer audioURL={model.audio.media} />
          )}

          {/* Contenuto principale */}
          <div
            className="text-container"
            dangerouslySetInnerHTML={{ __html: model?.body }}
          />

          {/* Immagine in posizione end, non specificata o stringa vuota */}
          {model?.image?.media &&
            (model?.image?.position === "end" ||
              !model?.image?.position ||
              model?.image?.position === "") && (
              <Box
                sx={{
                  my: 4,
                  borderRadius: 2,
                  overflow: "hidden",
                  boxShadow: 3,
                  display: "inline-block",
                  maxWidth: "100%",
                  mx: "auto",
                  lineHeight: 0,
                }}
              >
                <OptimizedImage
                  src={model.image.media}
                  alt={model.title}
                  width={"auto"}
                  height={"auto"}
                  loading="lazy"
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    display: "block",
                    transition: "transform 0.3s ease-in-out",
                  }}
                  imgProps={{
                    onMouseOver: (e) =>
                      (e.currentTarget.style.transform = "scale(1.02)"),
                    onMouseOut: (e) =>
                      (e.currentTarget.style.transform = "scale(1)"),
                  }}
                />
              </Box>
            )}

          {/* Audio in posizione end, non specificata o stringa vuota */}
          {model?.audio?.media &&
            (model?.audio?.position === "end" ||
              !model?.audio?.position ||
              model?.audio?.position === "") && (
              <CustomAudioPlayer audioURL={model.audio.media} />
            )}
        </Box>

        {/* Video (sempre dopo il contenuto) */}
        {model?.video && model?.video !== "" && (
          <YoutubePlayer videoUrl={model.video} />
        )}

        {/* Share Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            my: 4,
            py: 3,
            borderTop: "1px solid #eee",
            borderBottom: "1px solid #eee",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              color: "text.primary",
              fontSize: "1.5rem",
              fontWeight: 400,
              textAlign: "center",
              lineHeight: 1.4,
              letterSpacing: "0.01em",
            }}
          >
            Ti è piaciuto questo articolo?
            <br />
            Condividilo con chi potrebbe trovarlo utile!
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              onClick={shareOnWhatsapp}
              sx={{
                backgroundColor: "#25D366",
                color: "white",
                "&:hover": { backgroundColor: "#128C7E" },
              }}
            >
              <WhatsApp />
            </IconButton>
            <IconButton
              onClick={shareOnFacebook}
              sx={{
                backgroundColor: "#4267B2",
                color: "white",
                "&:hover": { backgroundColor: "#365899" },
              }}
            >
              <Facebook />
            </IconButton>
            <IconButton
              onClick={shareOnInstagram}
              sx={{
                backgroundColor: "#E1306C",
                color: "white",
                "&:hover": { backgroundColor: "#D1226C" },
              }}
            >
              <Instagram />
            </IconButton>
          </Box>
        </Box>
      </section>

      {/* Comments Section */}
      <Box
        sx={{ maxWidth: 800, mx: "auto", px: { xs: 2, sm: 3 }, mt: 1, mb: 2 }}
      >
        <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
          <CommentIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h5" component="h2" fontWeight={600}>
            Commenti
          </Typography>
        </Box>

        <CommentForm onSubmit={submitComment} isSubmitting={isSubmitting} />

        {/* Comments List */}
        {!model.comments ? (
          // Skeleton loading
          <Box>
            {[1, 2].map((i) => (
              <Paper
                key={i}
                elevation={0}
                sx={{ p: 2, mb: 2, bgcolor: "background.default" }}
              >
                <Box display="flex" gap={2}>
                  <Box flexGrow={1}>
                    <Typography variant="body1">
                      <Box component="span" fontWeight={600}>
                        Caricamento...
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : model.comments.filter((c) => c.published).length > 0 ? (
          // Published comments
          <Box mt={4}>
            {[...model.comments]
              .filter((comment) => comment.published)
              .sort((a, b) => {
                // Converti i timestamp in numeri per il confronto
                const dateA = a.timestamp?.toDate
                  ? a.timestamp.toDate().getTime()
                  : 0;
                const dateB = b.timestamp?.toDate
                  ? b.timestamp.toDate().getTime()
                  : 0;
                return dateB - dateA; // Ordine decrescente (dal più recente)
              })
              .map((comment, index) => (
                <Paper
                  key={index}
                  elevation={hoveredComment === index ? 2 : 0}
                  onMouseEnter={() => setHoveredComment(index)}
                  onMouseLeave={() => setHoveredComment(null)}
                  sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    transition: "all 0.2s ease-in-out",
                    transform:
                      hoveredComment === index ? "translateY(-2px)" : "none",
                    boxShadow:
                      hoveredComment === index ? theme.shadows[3] : "none",
                    "&:hover": {
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        sx={{
                          bgcolor: "primary.main",
                          color: "white",
                          width: 48,
                          height: 48,
                          fontSize: "1rem",
                          mt: 0.5,
                        }}
                      >
                        {getInitials(comment.firstName, comment.lastName)}
                      </Avatar>
                      <Box flexGrow={1}>
                        <Box
                          display="flex"
                          flexWrap="wrap"
                          alignItems="center"
                          gap={1}
                          mb={1.5}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            color="text.primary"
                          >
                            {comment.firstName} {comment.lastName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <Box component="span" mx={0.5}>
                              •
                            </Box>
                            {formatDate(comment.timestamp)}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body1"
                          color="text.primary"
                          paragraph
                        >
                          {comment.text}
                        </Typography>
                      </Box>
                    </Box>
                    {comment.response && (
                      <Box
                        sx={{
                          position: "relative",
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          p: 3,
                          bgcolor: "background.paper",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                          overflow: "hidden",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: "4px",
                            bgcolor: "primary.main",
                          },
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={2} mb={2}>
                          <Box
                            component="img"
                            src={authorImage}
                            alt="Ilaria Stumpo"
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              objectFit: "cover",
                              border: "2px solid",
                              borderColor: "primary.main",
                              mr: 1,
                            }}
                          />
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 1,
                              color: "primary.main",
                            }}
                          >
                            Risposta dell&apos;autore
                          </Typography>
                        </Box>
                        <Typography
                          variant="body1"
                          color="text.primary"
                          sx={{
                            lineHeight: 1.7,
                            "&:not(:last-child)": {
                              mb: 2,
                            },
                          }}
                        >
                          {comment.response}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}
          </Box>
        ) : (
          // No comments
          <Box
            textAlign="center"
            py={4}
            sx={{
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 2,
              mt: 4,
            }}
          >
            <CommentIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              Ancora nessun commento. Sii il primo a commentare!
            </Typography>
          </Box>
        )}
      </Box>
      <Footer />
    </>
  );
};

export default Article;
